import React, { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { Copy, Check } from "lucide-react";
import { Button, Input } from "../../components/legacy-ui.js";
import { createEmployeeAdvance, getBcvRate, getHistoricalRates, getPaymentMethods } from "../../utils/api.js";
import { formatCurrency } from "../../utils/formatters.js";
import { filterPaymentMethodsByCurrency } from "../../utils/paymentUtils.js";
import toast from "react-hot-toast";

const formatBsForCopy = (value) =>
  Number(value).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatAmountForDisplay = (value) => {
  if (!value) return "";
  const parts = value.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

const formatRate = (rate) => {
  if (!rate && rate !== 0) return "";
  return Number(rate).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formSelectClass =
  "w-full p-3 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none";

const parseAmountInput = (value) => {
  let raw = value.replace(/,/g, "");
  if (/^\d{1,3}(\.\d{3})+$/.test(raw)) raw = raw.replace(/\./g, "");
  if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) return raw;
  return null;
};

export default function EmployeeAdvanceForm({
  employee,
  weekStart,
  payday = 6,
  onSuccess,
}) {
  const [amount, setAmount] = useState("");
  const [amountUsd, setAmountUsd] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [useBcv, setUseBcv] = useState(true);
  const [enterInUsd, setEnterInUsd] = useState(false);
  const [copiedBs, setCopiedBs] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [reference, setReference] = useState("");
  const [skipNextWeek, setSkipNextWeek] = useState(Boolean(employee?.skipNextWeek));
  const [filteredMethods, setFilteredMethods] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [currentBcvRate, setCurrentBcvRate] = useState(null);
  const [selectedRate, setSelectedRate] = useState("");
  const [historicalRates, setHistoricalRates] = useState([]);
  const [paymentDate, setPaymentDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [rateDate, setRateDate] = useState("");

  const pendingAmount = Number(employee?.toPay) || 0;

  useEffect(() => {
    const load = async () => {
      try {
        const [bcvRes, methodsRes, histRes] = await Promise.all([
          getBcvRate(),
          getPaymentMethods(),
          getHistoricalRates(),
        ]);
        setCurrentBcvRate(bcvRes.data?.data?.rate);
        setSelectedRate(bcvRes.data?.data?.rate || "");
        const methods = methodsRes.data?.data || [];
        setPaymentMethods(methods);
        if (methods.length > 0) setPaymentMethod(methods[0]._id);
        const unique = (histRes.data?.usd || []).reduce((acc, current) => {
          if (!acc.find((item) => item.last_update === current.last_update && item.price === current.price)) {
            acc.push(current);
          }
          return acc;
        }, []);
        const sorted = unique.sort((a, b) => new Date(b.last_update) - new Date(a.last_update));
        setHistoricalRates(sorted);
        if (sorted.length > 0) setRateDate(sorted[0].last_update.slice(0, 10));
      } catch (e) {
        setError("No se pudieron cargar métodos de pago o tasas.");
      }
    };
    load();
  }, []);

  const ratesByDate = useMemo(() => {
    const map = {};
    historicalRates.forEach((rate) => {
      const date = rate.last_update.slice(0, 10);
      if (!map[date]) map[date] = rate.price;
    });
    return map;
  }, [historicalRates]);

  const availableDates = useMemo(
    () => Object.keys(ratesByDate).sort((a, b) => new Date(b) - new Date(a)),
    [ratesByDate],
  );

  useEffect(() => {
    if (currency === "BS") setSelectedRate(ratesByDate[rateDate] || "");
    else setSelectedRate(currentBcvRate || "");
  }, [currency, rateDate, ratesByDate, currentBcvRate]);

  useEffect(() => {
    const methods = filterPaymentMethodsByCurrency(paymentMethods, currency);
    setFilteredMethods(methods);
    if (!methods.some((m) => m._id === paymentMethod)) {
      setPaymentMethod(methods[0]?._id || "");
    }
  }, [currency, paymentMethods, paymentMethod]);

  const suggestAmount = (curr, pending, rate) => {
    if (!pending || pending <= 0) return "";
    if (curr === "USD") return Number(pending.toFixed(2)).toString();
    if (curr === "BS" && rate) return Number((pending * rate).toFixed(2)).toString();
    return "";
  };

  const applyUsdToBs = (usdRaw, rate) => {
    const usd = parseFloat(usdRaw) || 0;
    if (!rate || usd <= 0) {
      setAmount("");
      return;
    }
    setAmount(Number((usd * rate).toFixed(2)).toString());
  };

  useEffect(() => {
    if (!employee) return;
    setSkipNextWeek(Boolean(employee.skipNextWeek));
    const rate = currency === "BS" ? ratesByDate[rateDate] || selectedRate : null;
    if (currency === "BS" && useBcv && enterInUsd) {
      const usd = pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "";
      setAmountUsd(usd);
      applyUsdToBs(usd, rate);
    } else if (currency === "BS" && !useBcv) {
      setAmount("");
      setAmountUsd(pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "");
    } else {
      setAmount(suggestAmount(currency, pendingAmount, rate));
      setAmountUsd("");
    }
    // Solo al cambiar empleado o pendiente; moneda/tasa se manejan en sus handlers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.id, pendingAmount]);

  const handleCurrencyChange = (newCurrency) => {
    setCurrency(newCurrency);
    if (newCurrency !== "BS") {
      setUseBcv(true);
      setEnterInUsd(false);
      setAmountUsd("");
      setAmount(suggestAmount(newCurrency, pendingAmount, null));
      return;
    }
    const rate = ratesByDate[rateDate] || selectedRate;
    if (!useBcv) {
      setAmount("");
      setAmountUsd(pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "");
    } else if (enterInUsd) {
      const usd = pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "";
      setAmountUsd(usd);
      applyUsdToBs(usd, rate);
    } else {
      setAmount(suggestAmount(newCurrency, pendingAmount, rate));
      setAmountUsd("");
    }
  };

  const handleUseBcvChange = (checked) => {
    setUseBcv(checked);
    if (checked) {
      const rate = ratesByDate[rateDate] || selectedRate;
      if (enterInUsd) {
        const usd =
          amountUsd ||
          (pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "");
        setAmountUsd(usd);
        applyUsdToBs(usd, rate);
      } else {
        setAmount(suggestAmount("BS", pendingAmount, rate));
        setAmountUsd("");
      }
    } else {
      setEnterInUsd(false);
      setAmountUsd(pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "");
    }
  };

  const handleEnterInUsdChange = (checked) => {
    setEnterInUsd(checked);
    const rate = ratesByDate[rateDate] || selectedRate;
    if (checked) {
      const usd = pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "";
      setAmountUsd(usd);
      applyUsdToBs(usd, rate);
    } else {
      setAmountUsd("");
      setAmount(suggestAmount("BS", pendingAmount, rate));
    }
  };

  const handleRateDateChange = (newDate) => {
    setRateDate(newDate);
    if (currency === "BS" && useBcv) {
      const rate = ratesByDate[newDate];
      if (enterInUsd) {
        applyUsdToBs(amountUsd, rate);
      } else if (rate && pendingAmount > 0) {
        setAmount(Number((pendingAmount * rate).toFixed(2)).toString());
      }
    }
  };

  const handleAmountChange = (e) => {
    const parsed = parseAmountInput(e.target.value);
    if (parsed !== null) setAmount(parsed);
  };

  const handleAmountUsdChange = (e) => {
    const parsed = parseAmountInput(e.target.value);
    if (parsed !== null) {
      setAmountUsd(parsed);
      if (currency === "BS" && useBcv && enterInUsd) {
        applyUsdToBs(parsed, ratesByDate[rateDate] || selectedRate);
      }
    }
  };

  const numericAmount = parseFloat(amount) || 0;
  const numericAmountUsd = parseFloat(amountUsd) || 0;
  const rateNum = parseFloat(selectedRate) || 0;
  const manualRate =
    currency === "BS" && !useBcv && numericAmount > 0 && numericAmountUsd > 0
      ? numericAmount / numericAmountUsd
      : 0;

  const handleCopyBsAmount = async () => {
    if (!(numericAmount > 0)) return;
    try {
      await navigator.clipboard.writeText(formatBsForCopy(numericAmount));
      setCopiedBs(true);
      toast.success("Monto en Bs copiado", { position: "top-center" });
      setTimeout(() => setCopiedBs(false), 2000);
    } catch {
      toast.error("No se pudo copiar", { position: "top-center" });
    }
  };

  const amountInUSD =
    currency === "USD"
      ? numericAmount || null
      : currency === "BS" && (!useBcv || enterInUsd)
        ? numericAmountUsd || null
        : currency === "BS" && rateNum > 0 && numericAmount
          ? numericAmount / rateNum
          : null;
  let remainingUSD;
  if (employee && amountInUSD !== null) remainingUSD = pendingAmount - amountInUSD;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employee || !numericAmount) return;
    if (currency === "BS" && useBcv && !selectedRate) {
      setError("No hay tasa de cambio disponible para la fecha seleccionada.");
      return;
    }
    if (currency === "BS" && (!useBcv || enterInUsd) && !(numericAmountUsd > 0)) {
      setError("Indica el monto en dólares.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        employee: employee.id,
        amount: numericAmount,
        currency,
        paymentMethod,
        reference,
        paymentDate: `${paymentDate}T12:00:00`,
        weekStart,
        closeDay: payday,
        skipNextWeek,
      };
      if (currency === "BS") {
        payload.exchangeRate = useBcv ? selectedRate : numericAmount / numericAmountUsd;
        if (useBcv) payload.rateDate = rateDate;
      }
      await createEmployeeAdvance(payload);
      toast.success("Abono registrado", { position: "top-center" });
      onSuccess?.();
    } catch (err) {
      const message = err.response?.data?.error || "Error al registrar el abono.";
      setError(message);
      toast.error(message, { position: "top-center" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!employee) return null;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-gray-100 p-3 rounded-lg text-sm space-y-1">
        <p>
          Empleado: <strong>{employee.name}</strong>
        </p>
        <p className="text-red-600">
          Pendiente esta semana: <strong>{formatCurrency(pendingAmount)}</strong>
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Moneda</label>
        <select className={formSelectClass} value={currency} onChange={(e) => handleCurrencyChange(e.target.value)}>
          <option value="USD">Dólares ($)</option>
          <option value="BS">Bolívares (Bs)</option>
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Fecha del abono</label>
        <input
          type="date"
          required
          className={formSelectClass}
          value={paymentDate}
          onChange={(e) => {
            setPaymentDate(e.target.value);
            if (currency === "BS" && useBcv && ratesByDate[e.target.value]) setRateDate(e.target.value);
          }}
        />
      </div>

      {currency === "BS" && (
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              checked={useBcv}
              onChange={(e) => handleUseBcvChange(e.target.checked)}
            />
            Usar tasa BCV
          </label>
          {useBcv && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                checked={enterInUsd}
                onChange={(e) => handleEnterInUsdChange(e.target.checked)}
              />
              Ingresar monto en $
            </label>
          )}
        </div>
      )}

      {currency === "BS" && useBcv && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Fecha de la Tasa</label>
            <input
              type="date"
              className={formSelectClass}
              value={rateDate}
              onChange={(e) => handleRateDateChange(e.target.value)}
              min={availableDates.length > 0 ? availableDates[availableDates.length - 1] : ""}
              max={availableDates.length > 0 ? availableDates[0] : ""}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tasa BCV</label>
            <input
              type="text"
              readOnly
              className={`${formSelectClass} bg-gray-100`}
              value={selectedRate ? formatRate(selectedRate) : ""}
            />
          </div>
        </div>
      )}

      {currency === "BS" && useBcv && enterInUsd ? (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Monto en $</label>
            <input
              type="text"
              inputMode="decimal"
              required
              className={formSelectClass}
              value={formatAmountForDisplay(amountUsd)}
              onChange={handleAmountUsdChange}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Monto en Bs (según tasa)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                className={`${formSelectClass} bg-gray-100 font-semibold text-gray-800 flex-1`}
                value={numericAmount > 0 ? formatAmountForDisplay(amount) : ""}
                placeholder="Se calcula automáticamente"
              />
              <button
                type="button"
                onClick={handleCopyBsAmount}
                disabled={!(numericAmount > 0)}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Copiar monto en Bs"
              >
                {copiedBs ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                {copiedBs ? "Copiado" : "Copiar"}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Este es el monto que se registra y puedes pegarlo en la transferencia.
            </p>
          </div>
          {amountInUSD !== null && remainingUSD !== undefined && (
            <p className="text-xs text-gray-600">
              Pendiente restante: <strong>{formatCurrency(remainingUSD)}</strong>
            </p>
          )}
        </div>
      ) : (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Monto ({currency === "BS" ? "Bs" : "USD"})
          </label>
          <input
            type="text"
            inputMode="decimal"
            required
            className={formSelectClass}
            value={formatAmountForDisplay(amount)}
            onChange={handleAmountChange}
          />
          {currency === "BS" && useBcv && amountInUSD !== null && numericAmount > 0 && (
            <p className="text-xs text-gray-500 mt-1">Equivalente a: {formatCurrency(amountInUSD, "USD")}</p>
          )}
          {amountInUSD !== null && remainingUSD !== undefined && (
            <p className="text-xs text-gray-600 mt-1">
              Pendiente restante: <strong>{formatCurrency(remainingUSD)}</strong>
            </p>
          )}
        </div>
      )}

      {currency === "BS" && !useBcv && (
        <div className="space-y-3">
          <Input
            label="Monto $"
            type="text"
            inputMode="decimal"
            value={formatAmountForDisplay(amountUsd)}
            onChange={handleAmountUsdChange}
          />
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tasa usada (Bs/$)</label>
            <input
              type="text"
              readOnly
              className={`${formSelectClass} bg-gray-100`}
              value={manualRate > 0 ? formatRate(manualRate) : ""}
            />
          </div>
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Método de Pago</label>
        <select
          className={formSelectClass}
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
        >
          <option value="">Sin método</option>
          {filteredMethods.map((method) => (
            <option key={method._id} value={method._id}>
              {method.name}
            </option>
          ))}
        </select>
      </div>

      <Input
        label="Referencia"
        type="text"
        value={reference}
        onChange={(e) => setReference(e.target.value)}
      />

      <label className="flex items-start gap-2 text-sm text-gray-700 bg-amber-50 border border-amber-100 rounded-lg p-3">
        <input
          type="checkbox"
          className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          checked={skipNextWeek}
          onChange={(e) => setSkipNextWeek(e.target.checked)}
        />
        <span>
          <strong>No cobra la próxima semana.</strong> El restante queda como pendiente y la
          semana siguiente no suma salario (a menos que lo edites).
        </span>
      </label>

      {error && <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</p>}

      <Button
        type="submit"
        className="w-full"
        disabled={
          !amount ||
          submitting ||
          (currency === "BS" && useBcv && !selectedRate) ||
          (currency === "BS" && (!useBcv || enterInUsd) && !amountUsd)
        }
      >
        {submitting ? "Registrando…" : "Registrar abono"}
      </Button>
    </form>
  );
}
